import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  limit,
  updateDoc,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import OrderHeader from '../../components/orders/OrderHeader';
import OrderItems from '../../components/orders/OrderItems';
import OrderSummary from '../../components/orders/OrderSummary';
import OrderComments from '../../components/orders/OrderComments';
import OrderActions from '../../components/orders/OrderActions';
import ShippingInfo from '../../components/orders/ShippingInfo';
import CancelOrderModal from '../../components/orders/CancelOrderModal';
import ShareOrderButton from '../../components/orders/ShareOrderButton';
import DisputeManagement from '../../components/orders/DisputeManagement';
import type { Order } from '../../types';

export default function MarketplaceOrderDetails() {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [disputeUpdated, setDisputeUpdated] = useState(false);
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});
  const [updating, setUpdating] = useState(false);
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.role === 'admin' && id) {
      loadOrder();

      // Listen for cancellation requests in real-time
      const q = query(
        collection(db, 'cancellation_requests'),
        where('orderId', '==', id),
        where('status', '==', 'pending'),
        limit(1)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.size > 0) {
          setOrder(prev => prev ? { ...prev, hasCancellationRequest: true } : null);
        } else {
          setOrder(prev => prev ? { ...prev, hasCancellationRequest: false } : null);
        }
      });

      return () => unsubscribe();
    }
  }, [id, disputeUpdated]);

  useEffect(() => {
    const fetchProductImages = async () => {
      if (!order) return;

      console.log('Order:', order);
      console.log('Order source:', order.source);
      console.log('Order sourceData:', order.sourceData);

      // Se for um pedido WooCommerce, as imagens estão em sourceData.line_items
      if (order.source === 'woocommerce' && order.sourceData?.line_items) {
        console.log('WooCommerce line items:', order.sourceData.line_items);
        
        const wooImages: {[key: string]: string} = {};
        order.sourceData.line_items.forEach((item: any) => {
          console.log('Processing WooCommerce item:', item);
          console.log('Item product_id:', item.product_id);
          console.log('Item image:', item.image);
          
          if (item.image?.src) {
            const productId = item.product_id.toString();
            wooImages[productId] = item.image.src;
            console.log(`Added image for product ${productId}:`, item.image.src);
          }
        });
        
        console.log('Final WooCommerce images map:', wooImages);
        setProductImages(wooImages);
        return;
      }

      // Se for uma venda privada ou manual, busca do Firebase
      const items = order.items || [];
      const productIds = items
        .filter(item => item.productId && !item.image && !item.images?.[0]?.src)
        .map(item => item.productId);

      if (productIds.length === 0) return;

      try {
        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('id', 'in', productIds));
        const snapshot = await getDocs(q);
        
        const images: {[key: string]: string} = {};
        snapshot.forEach(doc => {
          const product = doc.data();
          if (product.image || product.images?.[0]?.src || product.mainImage) {
            images[product.id] = product.image || product.images?.[0]?.src || product.mainImage;
          }
        });

        setProductImages(images);
      } catch (error) {
        console.error('Error fetching product images:', error);
      }
    };

    fetchProductImages();
  }, [order]);

  const loadOrder = async () => {
    if (!id) return;

    try {
      const orderDoc = await getDoc(doc(db, 'orders', id));
      if (!orderDoc.exists()) {
        setError('Encomenda não encontrada');
        return;
      }

      const orderData = orderDoc.data();
      console.log('Raw Order Data:', orderData);

      let processedOrder;

      // Process WooCommerce order data
      if (orderData.source === 'woocommerce' && orderData.sourceData) {
        const shippingTotal = parseFloat(orderData.sourceData.shipping_total || '0');
        const shippingTax = parseFloat(orderData.sourceData.shipping_tax || '0');
        const subtotal = orderData.sourceData.line_items?.reduce((acc: number, item: any) => 
          acc + (parseFloat(item.subtotal) || 0), 0) || 0;

        processedOrder = {
          ...orderData,
          id: orderDoc.id,
          displayOrderId: orderData.sourceData.number || orderData.sourceOrderId,
          status: orderData.status || 'pending',
          customer: {
            name: `${orderData.sourceData.billing?.first_name || ''} ${orderData.sourceData.billing?.last_name || ''}`.trim() || 
                  `${orderData.sourceData.shipping?.first_name || ''} ${orderData.sourceData.shipping?.last_name || ''}`.trim(),
            email: orderData.sourceData.billing?.email || orderData.customer?.email,
            phone: orderData.sourceData.billing?.phone || orderData.customer?.phone
          },
          items: orderData.sourceData.line_items?.map((item: any) => ({
            name: item.name,
            quantity: item.quantity,
            price: parseFloat(item.price || '0'),
            total: parseFloat(item.total || '0'),
            image: item.image?.src,
            productId: item.product_id?.toString()
          })) || [],
          total: parseFloat(orderData.sourceData.total || '0'),
          subtotal: subtotal,
          shipping_total: shippingTotal,
          shipping_tax: shippingTax,
          shippingTotal: shippingTotal + shippingTax,
          shippingCost: shippingTotal + shippingTax,
          shippingMethod: orderData.sourceData.shipping_lines?.[0]?.method_title || 'Normal'
        };
      } else {
        // Process other orders (marketplace, private sale, manual)
        processedOrder = {
          ...orderData,
          id: orderDoc.id,
          displayOrderId: orderData.orderNumber || orderData.id,
          status: orderData.status || 'pending',
          customer: {
            name: orderData.customer?.name || orderData.buyerName,
            email: orderData.customer?.email,
            phone: orderData.customer?.phone,
            address: orderData.shippingAddress || orderData.customer?.address
          },
          items: (orderData.items || []).map((item: any) => ({
            ...item,
            image: item.image || item.product?.image || item.product?.mainImage || item.product?.images?.[0]?.src,
            productId: item.productId?.toString() || item.product?.id
          })),
          total: parseFloat(orderData.total || '0'),
          subtotal: parseFloat(orderData.subtotal || '0'),
          shippingCost: parseFloat(orderData.shippingCost || '0'),
          shippingMethod: orderData.shippingMethod || 'Normal'
        };
      }

      console.log('Processed Order:', processedOrder);
      setOrder(processedOrder);
      setLoading(false);
    } catch (error) {
      console.error('Error loading order:', error);
      setError('Erro ao carregar a encomenda');
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!order || updating) return;

    try {
      setUpdating(true);
      const orderRef = doc(db, 'orders', order.id);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Reload the order data
      await loadOrder();
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Erro ao atualizar o estado da encomenda');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!order || updating) return;

    try {
      setUpdating(true);
      const orderRef = doc(db, 'orders', order.id);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Reload the order data
      await loadOrder();
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Erro ao atualizar o estado da encomenda');
    } finally {
      setUpdating(false);
    }
  };

  const getOrderTitle = () => {
    if (order?.source === 'woocommerce') {
      return `Encomenda #${order.displayOrderId}`;
    } else if (order?.isOnlineSale) {
      return `Encomenda ${order.orderNumber}`;
    } else {
      return `Encomenda Manual ${order.orderNumber}`;
    }
  };

  const getShippingMethod = () => {
    if (order?.source === 'woocommerce') {
      return order.sourceData?.shipping_lines?.[0]?.method_title || 'Normal';
    } else {
      return 'Normal';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm mx-6 my-6">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold">{getOrderTitle()}</h1>
              <div className="mt-1 text-sm text-gray-500">
                Criada em {new Date(order?.createdAt || '').toLocaleString('pt-PT')}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                order?.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                order?.status === 'shipped' ? 'bg-green-100 text-green-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {order?.status === 'cancelled' ? 'Cancelado' :
                 order?.status === 'shipped' ? 'Enviado' : 'Pendente'}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Dados do Cliente */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Dados do Cliente</h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div><span className="font-medium">Nome:</span> {order?.customer?.name}</div>
                <div><span className="font-medium">Email:</span> {order?.customer?.email}</div>
                <div><span className="font-medium">Telefone:</span> {order?.customer?.phone}</div>
              </div>
            </div>

            {/* Morada de Envio */}
            <div>
              <h2 className="text-xl font-semibold mb-4">O vendedor irá enviar para:</h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="font-medium">{order?.customer?.name}</div>
                <div>{order?.customer?.address?.street}</div>
                {order?.customer?.address?.complement && (
                  <div>{order.customer.address.complement}</div>
                )}
                <div>
                  {order?.customer?.address?.postalCode} {order?.customer?.address?.city}
                </div>
                <div>{order?.customer?.address?.country}</div>
                <div>Tel: {order?.customer?.phone}</div>
                <div className="mt-2">
                  Método de envio: {getShippingMethod()}
                </div>
              </div>
            </div>
          </div>

          {/* Itens da Encomenda */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Itens da Encomenda</h2>
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 w-20">IMAGEM</th>
                  <th className="text-left py-2">PRODUTO</th>
                  <th className="text-center py-2 w-24">QTD</th>
                  <th className="text-right py-2 w-32">PREÇO</th>
                  <th className="text-right py-2 w-32">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {order?.items?.map((item, index) => {
                  const imageUrl = item.image || 
                                 (item.images?.[0]?.src) || 
                                 (item.productId && productImages[item.productId]);
                  
                  return (
                    <tr key={index} className="border-b">
                      <td className="py-4">
                        {imageUrl ? (
                          <img 
                            src={imageUrl}
                            alt={item.name}
                            className="w-16 h-16 object-contain"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.src = '/placeholder-product.png';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 flex items-center justify-center">
                            <span className="text-gray-400">Sem imagem</span>
                          </div>
                        )}
                      </td>
                      <td className="py-4">
                        <div className="font-medium">{item.name}</div>
                        {item.sku && <div className="text-sm text-gray-500">SKU: {item.sku}</div>}
                      </td>
                      <td className="py-4 text-center">{item.quantity}</td>
                      <td className="py-4 text-right">€{item.price?.toFixed(2)}</td>
                      <td className="py-4 text-right">€{(item.quantity * (item.price || 0)).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Resumo do Pedido */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Resumo do Pedido</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>€{order?.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Portes de Envio:</span>
                  <span>€{order?.shippingCost?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t mt-2">
                  <span>Total:</span>
                  <span>€{order?.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Comentários */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Comentários</h2>
            <OrderComments
              orderId={id || ''}
              comments={order?.comments || []}
              onCommentAdded={() => loadOrder()}
            />
          </div>

          {/* Gestão de Reclamação */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Gestão de Reclamação</h2>
            <DisputeManagement
              orderId={id || ''}
              onUpdate={() => setDisputeUpdated(prev => !prev)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
